<template>
  <div>
    <!-- Loading -->
    <div v-if="loading" class="flex justify-center py-16 text-gray-400">
      <svg class="h-6 w-6 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
      </svg>
    </div>

    <p v-else-if="!course" class="py-12 text-center text-sm text-gray-500">
      اطلاعات این دوره در دسترس نیست.
    </p>

    <div v-else class="space-y-5">
      <!-- Cover & intro video -->
      <div v-if="course.cover_image_url || course.intro_video_url" class="grid gap-3 sm:grid-cols-2">
        <img
          v-if="course.cover_image_url"
          :src="course.cover_image_url"
          class="h-44 w-full rounded-xl border border-gray-100 dark:border-gray-700 object-cover"
          alt=""
        />
        <div v-if="course.intro_video_url" class="rounded-xl border border-gray-100 dark:border-gray-700 p-3">
          <p class="mb-2 text-xs font-semibold text-gray-500 dark:text-gray-400">ویدیو معرفی دوره</p>
          <VideoPlayer :url="course.intro_video_url" />
        </div>
      </div>

      <!-- Stats -->
      <div class="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <div v-for="tile in statTiles" :key="tile.label" class="rounded-xl border border-gray-100 dark:border-gray-700 px-3 py-2.5">
          <p class="text-[11px] text-gray-400">{{ tile.label }}</p>
          <p class="mt-1 text-sm font-bold text-gray-900 dark:text-gray-100">{{ tile.value }}</p>
        </div>
      </div>

      <!-- Profile -->
      <div class="rounded-xl border border-gray-100 dark:border-gray-700 px-4 py-3">
        <p class="mb-2 text-xs font-semibold text-gray-500 dark:text-gray-400">مشخصات دوره</p>
        <dl class="grid gap-x-6 gap-y-2 sm:grid-cols-2">
          <div v-for="row in profileRows" :key="row.label" class="text-xs">
            <dt class="text-gray-400 mb-0.5">{{ row.label }}</dt>
            <dd class="text-gray-800 dark:text-gray-200 break-words whitespace-pre-line" :dir="row.ltr ? 'ltr' : null">
              {{ row.value || '—' }}
            </dd>
          </div>
        </dl>
      </div>

      <!-- Instructors -->
      <div v-if="course.instructors.length" class="rounded-xl border border-gray-100 dark:border-gray-700 px-4 py-3">
        <p class="mb-2 text-xs font-semibold text-gray-500 dark:text-gray-400">مدرسان</p>
        <div class="flex flex-wrap gap-2">
          <span
            v-for="instructor in course.instructors"
            :key="instructor.id"
            class="inline-flex items-center gap-2 rounded-full bg-gray-50 dark:bg-gray-700/50 py-1 pe-3 ps-1 text-xs text-gray-700 dark:text-gray-200"
          >
            <img v-if="instructor.avatar_url" :src="instructor.avatar_url" class="h-6 w-6 rounded-full object-cover" alt="" />
            <span
              v-else
              class="flex h-6 w-6 items-center justify-center rounded-full bg-gray-200 dark:bg-gray-600 text-[10px] font-semibold text-gray-500"
            >
              {{ initial(instructor.name) }}
            </span>
            {{ instructor.name }}
          </span>
        </div>
      </div>

      <!-- Syllabus -->
      <div>
        <div class="mb-2 flex items-center justify-between gap-2">
          <p class="text-xs font-semibold text-gray-500 dark:text-gray-400">سرفصل‌های دوره</p>
          <p v-if="chapters.length" class="text-xs text-gray-400">
            {{ toFa(stats.chapters_count) }} فصل · {{ toFa(stats.lessons_count) }} درس
          </p>
        </div>

        <p
          v-if="chapters.length === 0"
          class="rounded-xl border border-dashed border-gray-200 dark:border-gray-700 py-8 text-center text-sm text-gray-500"
        >
          برای این دوره هنوز سرفصلی ثبت نشده است.
        </p>

        <div v-else class="space-y-2">
          <div
            v-for="chapter in chapters"
            :key="chapter.id"
            class="overflow-hidden rounded-xl border border-gray-100 dark:border-gray-700"
          >
            <button
              type="button"
              @click="toggleChapter(chapter.id)"
              class="flex w-full items-center justify-between gap-3 bg-gray-50 dark:bg-gray-700/40 px-4 py-3 text-start hover:bg-gray-100 dark:hover:bg-gray-700/60 transition-colors"
            >
              <span class="min-w-0">
                <span class="block truncate text-sm font-semibold text-gray-800 dark:text-gray-100">
                  {{ toFa(chapter.order) }}. {{ chapter.title }}
                </span>
                <span class="mt-0.5 block text-xs text-gray-400">
                  {{ toFa(chapter.lessons.length) }} درس
                  <span v-if="chapter.instructor"> · {{ chapter.instructor }}</span>
                  <span v-if="chapter.is_free_preview"> · پیش‌نمایش رایگان</span>
                </span>
              </span>
              <span class="flex shrink-0 items-center gap-2">
                <span class="inline-flex items-center rounded-md bg-white dark:bg-gray-800 px-2 py-0.5 text-[11px] text-gray-500 dark:text-gray-300">
                  {{ chapter.status_label }}
                </span>
                <svg
                  :class="['h-4 w-4 text-gray-400 transition-transform', openChapters.includes(chapter.id) ? 'rotate-180' : '']"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </span>
            </button>

            <div v-if="openChapters.includes(chapter.id)">
              <p v-if="chapter.description" class="border-b border-gray-100 dark:border-gray-700/60 px-4 py-2.5 text-xs text-gray-500 whitespace-pre-line">
                {{ chapter.description }}
              </p>

              <p v-if="chapter.lessons.length === 0" class="px-4 py-4 text-center text-xs text-gray-400">
                این فصل هنوز درسی ندارد.
              </p>

              <div v-else class="divide-y divide-gray-100 dark:divide-gray-700/60">
                <div v-for="lesson in chapter.lessons" :key="lesson.id" class="px-4 py-3">
                  <div class="flex flex-wrap items-center justify-between gap-2">
                    <div class="min-w-0">
                      <p class="truncate text-sm text-gray-800 dark:text-gray-100">
                        {{ toFa(lesson.order) }}. {{ lesson.title }}
                      </p>
                      <p class="mt-0.5 text-xs text-gray-400">
                        {{ lesson.type_label }}
                        <span v-if="lesson.duration"> · {{ lesson.duration }}</span>
                        <span v-if="lesson.file_size"> · {{ lesson.file_size }}</span>
                        <span v-if="lesson.is_free_preview"> · پیش‌نمایش رایگان</span>
                      </p>
                    </div>

                    <div class="flex shrink-0 items-center gap-1.5">
                      <span class="inline-flex items-center rounded-md bg-gray-100 dark:bg-gray-700/60 px-2 py-0.5 text-[11px] text-gray-600 dark:text-gray-300">
                        {{ lesson.status_label }}
                      </span>

                      <button
                        v-if="lesson.video_url"
                        type="button"
                        @click="toggleLesson(lesson.id)"
                        class="inline-flex h-7 items-center gap-1 rounded-lg border border-gray-200 dark:border-gray-600 px-2 text-[11px] text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                      >
                        <svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                        {{ openLessons.includes(lesson.id) ? 'بستن ویدیو' : 'پخش ویدیو' }}
                      </button>

                      <span v-else class="text-[11px] text-gray-400">بدون ویدیو</span>
                    </div>
                  </div>

                  <p v-if="lesson.description" class="mt-1.5 text-xs text-gray-500 whitespace-pre-line">
                    {{ lesson.description }}
                  </p>

                  <VideoPlayer v-if="openLessons.includes(lesson.id)" :url="lesson.video_url" class="mt-2" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import jMoment from 'moment-jalaali'
import ApprovalVideoPreview from '@modules/system/templates/js/components/ApprovalVideoPreview.vue'

export default {
  name: 'KanoonCourseContent',

  components: {
    // The same player the approval diffs use, so an Aparat or YouTube lesson
    // plays inside the panel instead of sending the reviewer to another tab.
    VideoPlayer: ApprovalVideoPreview,
  },

  props: {
    courseId: { type: Number, required: true },
    endpoint: { type: String, default: '/dashboard-api/kanoon/courses' },
  },

  data: () => ({
    loading: true,
    detail: null,
    openChapters: [],
    openLessons: [],
  }),

  computed: {
    course() {
      return this.detail?.course ?? null
    },

    chapters() {
      return this.detail?.chapters ?? []
    },

    stats() {
      return this.detail?.stats ?? {}
    },

    statTiles() {
      const stats = this.stats

      return [
        { label: 'فصل‌ها', value: this.toFa(stats.chapters_count) },
        { label: 'درس‌ها', value: this.toFa(stats.lessons_count) },
        { label: 'درس‌های ویدیویی', value: this.toFa(stats.videos_count) },
        { label: 'ثبت‌نام', value: this.toFa(stats.enrollments_count) },
        { label: 'مدت دوره', value: stats.duration ?? '—' },
      ]
    },

    profileRows() {
      const course = this.course ?? {}

      return [
        { label: 'آموزشگاه', value: course.institute?.name },
        { label: 'دسته‌بندی', value: course.category },
        { label: 'نوع دوره', value: course.type_label },
        { label: 'سطح', value: course.level_label },
        { label: 'وضعیت', value: course.status_label },
        { label: 'قیمت', value: this.money(course.price) },
        { label: 'تخفیف', value: course.discount_percent ? `${this.toFa(course.discount_percent)}٪` : null },
        { label: 'قیمت نهایی', value: this.money(course.final_price) },
        { label: 'پیش‌نیاز دارد', value: course.has_prerequisite ? 'بله' : 'خیر' },
        { label: 'همراه با منتور', value: course.is_mentored ? 'بله' : 'خیر' },
        { label: 'تاریخ ایجاد', value: this.jalali(course.created_at), ltr: true },
        { label: 'توضیح کوتاه', value: course.description },
        { label: 'معرفی دوره', value: course.overview },
      ]
    },
  },

  watch: {
    courseId: 'fetch',
  },

  created() {
    this.fetch()
  },

  methods: {
    initial(name) {
      return (name ?? '').trim().charAt(0) || '?'
    },

    toFa(value) {
      if (value == null) return '۰'
      return Number(value).toLocaleString('fa-IR')
    },

    money(value) {
      if (value == null) return null
      return `${this.toFa(value)} تومان`
    },

    jalali(value) {
      if (!value) return null
      return jMoment(value, 'YYYY-MM-DD HH:mm:ss').format('jYYYY/jMM/jDD')
    },

    toggleChapter(chapterId) {
      const index = this.openChapters.indexOf(chapterId)
      if (index === -1) this.openChapters.push(chapterId)
      else this.openChapters.splice(index, 1)
    },

    toggleLesson(lessonId) {
      const index = this.openLessons.indexOf(lessonId)
      if (index === -1) this.openLessons.push(lessonId)
      else this.openLessons.splice(index, 1)
    },

    async fetch() {
      this.loading = true
      this.openLessons = []
      try {
        const { data } = await Nova.request().get(`${this.endpoint}/${this.courseId}`)
        this.detail = data
        // The first chapter opens on load so the syllabus is never a wall of
        // collapsed rows a reviewer has to click through.
        this.openChapters = data.chapters?.length ? [data.chapters[0].id] : []
      } catch (error) {
        this.detail = null
        Nova.error(error?.response?.data?.message ?? 'دریافت جزئیات دوره با خطا مواجه شد.')
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
/* The shared preview caps itself at 360px, which suits an inline approval diff.
   A course review has the room for a larger player, so widen it here only. */
:deep(.video-preview .video-frame) {
  max-width: 560px;
}
</style>
